import type { FormElement, TableElement } from '../model/form.types';
import type { FieldDefinition } from '../model/types';
import { getFieldDefinition } from './registry';
import { isCoveredCell } from './table-cell';

export interface ModelRowColumn {
  columnIndex: number;
  colSpan: number;
  element: FormElement | null;
  definition: FieldDefinition | null;
}

// Display-only address of an added-row cell node: it routes issues and focus on the sheet and
// never enters `values` or the stored snapshot, which stay positional.
export function extraCellNodeId(tableId: string, rowIndex: number, columnIndex: number): string {
  return `${tableId}:extra:${rowIndex}:${columnIndex}`;
}

export interface ExtraCellAddress {
  tableId: string;
  rowIndex: number;
  columnIndex: number;
}

export function parseExtraCellNodeId(nodeId: string): ExtraCellAddress | null {
  const match = /^(.+):extra:(\d+):(\d+)$/.exec(nodeId);
  if (!match) return null;
  return { tableId: match[1], rowIndex: Number(match[2]), columnIndex: Number(match[3]) };
}

// Removing a row renumbers the rows below it, so a selection at or below it would name another row.
export function selectionAfterRowShift(selectedId: string | null, tableId: string, fromRow: number): string | null {
  const address = selectedId ? parseExtraCellNodeId(selectedId) : null;
  return address?.tableId === tableId && address.rowIndex >= fromRow ? null : selectedId;
}

export function isMultipleTable(element: FormElement): element is TableElement {
  return element.type === 'TABLE' && (element as TableElement).properties.is_multiple === true;
}

// The model row is the LAST structure row: added rows copy its layout and field types. A last-row
// cell covered by a vertical merge has its root in an earlier row, so that column has no field of
// its own in the model row and cannot take a value in an added row.
export function modelRowColumns(table: TableElement): ModelRowColumn[] {
  const rows = table.properties.cells ?? [];
  const lastRow = rows[rows.length - 1] ?? [];
  return lastRow
    .filter((cell) => !isCoveredCell(cell))
    .map((cell) => {
      const content = cell.content ?? null;
      const definition = content ? getFieldDefinition(content) : null;
      return {
        columnIndex: cell.colIndex,
        colSpan: cell.colSpan,
        element: definition ? content : null,
        definition,
      };
    });
}

export function modelRowFieldByColumn(
  table: TableElement,
): Map<number, { element: FormElement; definition: FieldDefinition }> {
  const columns = new Map<number, { element: FormElement; definition: FieldDefinition }>();
  for (const column of modelRowColumns(table)) {
    if (column.element && column.definition) {
      columns.set(column.columnIndex, { element: column.element, definition: column.definition });
    }
  }
  return columns;
}
